import { GripVertical } from "lucide-react";
import { useEffect, useState } from "react";
import { Kanban } from "./Kanban";
import { copy } from "../copy";
import { sourceShort, topicTitle } from "../domain/display";
import { typeCovered } from "../domain/executors";
import {
  columnOverdue,
  FLOW_COLUMNS,
  flowColumn,
  hoursSince,
  matchesColumn,
  scoreLabel,
  stageAgeLabel,
  stageEnteredAt,
} from "../domain/flow";
import { countSoonReviews } from "../domain/reviewQueue";
import { matchesQuery, topicsForAccount } from "../domain/selectors";
import { activeTaskForTopic, taskChip, taskStatusLine } from "../domain/tasks";
import { useApp } from "../state/store";
import type { Topic, TopicStatus } from "../types";
import { CalendarView } from "./CalendarView";
import { PhoneReviewDrawer } from "./ReviewDesk";

const TABS = ["全部", ...FLOW_COLUMNS.map((column) => column.id)] as const;
type Tab = (typeof TABS)[number];

export function KanbanDesk() {
  const app = useApp();
  const params = new URLSearchParams(app.href.split("?")[1] ?? "");
  const filter = readTab(params.get("status"));
  const view = readView(params.get("view"));
  const now = new Date();
  const topics = topicsForAccount(app.state.topics, app.accountId).filter((topic) => matchesQuery(topic, app.query) && flowColumn(topic.status));
  const visible = sortLane(topics.filter((topic) => matchesColumn(topic, filter)), filter);
  const confirmed = visible.filter((topic) => topic.status === "已确认");
  const canDrag = filter === "制作中";
  const [dragId, setDragId] = useState<string | null>(null);
  const selected = topics.find((topic) => topic.id === params.get("topic")) ?? null;
  const review = topics.filter((topic) => topic.status === "待审阅");
  const soon = countSoonReviews(review, now);

  function search(changes: Record<string, string | null>) {
    const next = new URLSearchParams(app.href.split("?")[1] ?? "");
    next.delete("calendar");
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    return next.toString();
  }

  function dropOn(targetId: string) {
    if (!dragId || dragId === targetId) return;
    const ordered = confirmed.map((topic) => topic.id);
    const from = ordered.indexOf(dragId);
    const to = ordered.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const [item] = ordered.splice(from, 1);
    if (!item) return;
    ordered.splice(to, 0, item);
    app.reorderQueue(ordered);
    setDragId(null);
  }

  return (
    <div className="lane">
      {view === "board" ? <Kanban topics={topics} /> : null}
      {view === "calendar" ? (
        <CalendarPage
          topics={topics}
          soon={soon}
          reviewCount={review.length}
          onOpen={(topic) => app.navigate("kanban", search({ topic: topic.id }))}
        />
      ) : null}
      {view === "list" ? (
      <>
      <div className="lane-tabs" role="tablist" aria-label={copy.lane.status}>
        {TABS.map((id) => {
          const count = id === "全部" ? topics.length : topics.filter((topic) => matchesColumn(topic, id)).length;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={filter === id}
              onClick={() => app.navigate("kanban", search({ status: id === "全部" ? null : id }))}
            >
              {id === "全部" ? copy.desk.all : id}
              <em>{count}</em>
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <p className="lane-empty">
          {app.query.trim() ? copy.lane.emptySearch : filter === "全部" ? copy.lane.empty : copy.lane.emptyStep}
          <button
            type="button"
            className="text-action"
            onClick={() => {
              if (app.query.trim()) app.setQuery("");
              else if (filter !== "全部") app.navigate("kanban", search({ status: null }));
              else app.navigate("inbox");
            }}
          >
            {app.query.trim() ? copy.lane.clearSearch : filter === "全部" ? copy.lane.emptyLink : copy.lane.seeAll}
          </button>
        </p>
      ) : (
        <div className="lane-table" role="table" aria-label={copy.pages.kanban.title}>
          <div className={canDrag ? "lane-row lane-head has-handle" : "lane-row lane-head"} role="row">
            {canDrag ? <span /> : null}
            <span>{copy.lane.title}</span>
            <span>{copy.lane.status}</span>
            <span className="col-score">{copy.lane.score}</span>
            <span className="col-assistant">{copy.lane.assistant}</span>
            <span className="col-age">{copy.lane.age}</span>
            <span className="col-plan">{copy.lane.plan}</span>
          </div>
          {visible.map((topic) => (
            <LaneRow
              key={topic.id}
              topic={topic}
              now={now}
              handle={canDrag}
              draggable={canDrag && topic.status === "已确认"}
              dragging={dragId === topic.id}
              onOpen={() => app.navigate("kanban", search({ topic: topic.id }))}
              onDragStart={() => setDragId(topic.id)}
              onDragEnd={() => setDragId(null)}
              onDrop={() => dropOn(topic.id)}
            />
          ))}
        </div>
      )}

      </>
      ) : null}

      {selected && selected.status === "待审阅" ? (
        <PhoneReviewDrawer topic={selected} onClose={(nextId) => app.navigate("kanban", search({ topic: nextId }))} />
      ) : null}
      {selected && selected.status !== "待审阅" ? (
        <DetailDrawer topic={selected} now={now} onClose={() => app.navigate("kanban", search({ topic: null }))} />
      ) : null}
    </div>
  );
}

function LaneRow({
  topic,
  now,
  handle,
  draggable,
  dragging,
  onOpen,
  onDragStart,
  onDragEnd,
  onDrop,
}: {
  topic: Topic;
  now: Date;
  handle: boolean;
  draggable: boolean;
  dragging: boolean;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
}) {
  const app = useApp();
  const column = flowColumn(topic.status);
  const entered = stageEnteredAt(topic, app.state.activities);
  const overdue = column ? columnOverdue(column, hoursSince(entered, now)) : false;
  const linked = activeTaskForTopic(app.state.tasks ?? [], topic);
  const chip = linked ? taskChip(linked) : null;
  const sub = chip || (topic.status === "已确认" ? copy.lane.queued : "") || sourceShort(topic) || copy.flow.noAssistant;
  return (
    <div
      className={["lane-row", handle ? "has-handle" : "", dragging ? "is-dragging" : ""].filter(Boolean).join(" ")}
      role="row"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter") onOpen();
      }}
      onDragOver={draggable ? (event) => event.preventDefault() : undefined}
      onDrop={draggable ? (event) => { event.preventDefault(); onDrop(); } : undefined}
    >
      {handle ? (
        <span
          className={draggable ? "lane-handle" : "lane-handle is-quiet"}
          draggable={draggable}
          role={draggable ? "button" : undefined}
          tabIndex={-1}
          aria-label={draggable ? copy.lane.drag : undefined}
          onClick={(event) => event.stopPropagation()}
          onDragStart={draggable ? (event) => {
            event.stopPropagation();
            event.dataTransfer.setData("text/plain", topic.id);
            event.dataTransfer.effectAllowed = "move";
            onDragStart();
          } : undefined}
          onDragEnd={draggable ? onDragEnd : undefined}
        >
          {draggable ? <GripVertical size={14} /> : null}
        </span>
      ) : null}
      <span className="lane-title">
        <b>{topicTitle(topic)}</b>
        <small>{sub}</small>
      </span>
      <span className="lane-status"><i />{statusText(topic.status)}</span>
      <span className="col-score">{scoreLabel(app.state.postScores ?? [], topic.id)}</span>
      <span className="col-assistant">{sourceShort(topic) || copy.flow.noAssistant}</span>
      <span className={overdue ? "col-age is-late" : "col-age"}>{stageAgeLabel(entered, now)}</span>
      <span className="col-plan">{topic.plannedPublishDate || copy.dash}</span>
    </div>
  );
}

function DetailDrawer({ topic, now, onClose }: { topic: Topic; now: Date; onClose: () => void }) {
  const app = useApp();
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const linked = activeTaskForTopic(app.state.tasks ?? [], topic);
  const line = linked ? taskStatusLine(linked, app.agentReady, typeCovered(app.state.executors ?? [], linked.type)) : copy.lane.noTask;
  const entered = stageEnteredAt(topic, app.state.activities);

  function decide(status: "已确认" | "不做" | "已完成") {
    const previous = topic.status;
    app.setStatus(topic.id, status);
    const message = status === "已确认"
      ? copy.status.confirmed(topicTitle(topic))
      : status === "不做"
        ? copy.status.skipped(topicTitle(topic))
        : `「${topicTitle(topic)}」标为已完成`;
    app.notify(message, "info", { label: copy.undo, undo: { topicId: topic.id, status: previous } });
    onClose();
  }

  return (
    <div className="drawer-backdrop" onMouseDown={onClose}>
      <div className="sheet is-side" role="dialog" aria-modal="true" aria-label={topicTitle(topic)} onMouseDown={(event) => event.stopPropagation()}>
        <header className="sheet-head">
          <h2>{topicTitle(topic)}</h2>
          <button type="button" className="button ghost" onClick={onClose}>{copy.close}</button>
        </header>
        <dl className="lane-facts">
          <div><dt>{copy.lane.status}</dt><dd>{statusText(topic.status)}</dd></div>
          <div><dt>{copy.lane.score}</dt><dd>{scoreLabel(app.state.postScores ?? [], topic.id)}</dd></div>
          <div><dt>{copy.lane.assistant}</dt><dd>{sourceShort(topic) || copy.flow.noAssistant}</dd></div>
          <div><dt>{copy.lane.age}</dt><dd>{stageAgeLabel(entered, now)}</dd></div>
          <div><dt>{copy.lane.plan}</dt><dd>{topic.plannedPublishDate || copy.dash}</dd></div>
        </dl>
        {topic.status === "待确认" ? (
          <div className="row-actions">
            <button type="button" className="button primary" onClick={() => decide("已确认")}>{copy.doIt}</button>
            <button type="button" className="button ghost" onClick={() => decide("不做")}>{copy.skip}</button>
          </div>
        ) : null}
        {topic.status === "待发布" ? (
          <button type="button" className="button primary" onClick={() => decide("已完成")}>{copy.today.finish}</button>
        ) : null}
        {topic.status !== "待确认" && topic.status !== "待发布" ? <p>{line}</p> : null}
      </div>
    </div>
  );
}

function CalendarPage({
  topics,
  soon,
  reviewCount,
  onOpen,
}: {
  topics: Topic[];
  soon: number;
  reviewCount: number;
  onOpen: (topic: Topic) => void;
}) {
  const due = topics
    .filter((topic) => topic.status === "待发布")
    .sort((left, right) => (left.plannedPublishDate ?? "9999").localeCompare(right.plannedPublishDate ?? "9999") || topicTitle(left).localeCompare(topicTitle(right), "zh"));
  return (
    <div className="stack">
      <p className="muted">{soon > 0 ? copy.overview.reviewSummary(reviewCount, soon) : copy.lane.soonNone}</p>
      <CalendarView onOpen={onOpen} />
      <section className="lane-due" aria-label={copy.publish.queue}>
        <header>
          <h2>{copy.publish.queue}</h2>
          <span className="muted num">{due.length}</span>
        </header>
        {due.length === 0 ? <p className="muted">{copy.publish.empty}</p> : (
          <div className="lane-table">
            {due.map((topic) => (
              <button key={topic.id} type="button" className="lane-due-row" onClick={() => onOpen(topic)}>
                <b>{topicTitle(topic)}</b>
                <span>{topic.plannedPublishDate || copy.dash}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function readView(value: string | null): "list" | "board" | "calendar" {
  if (value === "board" || value === "calendar") return value;
  return "list";
}

function readTab(value: string | null): Tab {
  return TABS.includes(value as Tab) ? (value as Tab) : "全部";
}

function statusText(status: TopicStatus): string {
  if (status === "已完成") return "已发布";
  return status;
}

function sortLane(topics: Topic[], filter: Tab): Topic[] {
  return [...topics].sort((left, right) => {
    if (filter === "待确认") return rank(left) - rank(right) || right.updatedAt.localeCompare(left.updatedAt);
    if (filter === "待发布") return (left.plannedPublishDate ?? "9999").localeCompare(right.plannedPublishDate ?? "9999");
    if (filter === "已发布") return (right.actualPublishAt ?? "").localeCompare(left.actualPublishAt ?? "");
    if (filter === "待审阅") return right.updatedAt.localeCompare(left.updatedAt);
    const column = columnIndex(left) - columnIndex(right);
    if (column !== 0) return column;
    const stage = stageRank(left) - stageRank(right);
    if (stage !== 0) return stage;
    if (left.status === "已确认" && right.status === "已确认") return queue(left, right);
    return right.updatedAt.localeCompare(left.updatedAt);
  });
}

function rank(topic: Topic): number {
  return topic.recommendRank ?? Number.POSITIVE_INFINITY;
}

function columnIndex(topic: Topic): number {
  const column = flowColumn(topic.status);
  const index = FLOW_COLUMNS.findIndex((item) => item.id === column);
  return index < 0 ? FLOW_COLUMNS.length : index;
}

function stageRank(topic: Topic): number {
  if (topic.status === "已确认") return 0;
  if (topic.status === "制作中") return 1;
  return 2;
}

function queue(left: Topic, right: Topic): number {
  if (left.queueOrder == null && right.queueOrder == null) return topicTitle(left).localeCompare(topicTitle(right), "zh");
  if (left.queueOrder == null) return 1;
  if (right.queueOrder == null) return -1;
  if (left.queueOrder !== right.queueOrder) return left.queueOrder - right.queueOrder;
  return topicTitle(left).localeCompare(topicTitle(right), "zh");
}
